import * as React from 'react';
import { useMemo } from 'react';
import { Form, InputRef } from 'antd';
import Input from 'antd/lib/input';
import IMask from 'imask';
import { IMaskOptions, InputFieldProps } from './types';
import styles from './InputField.module.scss'

export const MaskedInput = React.forwardRef<InputRef, InputFieldProps>(
    function MaskedInput(props: InputFieldProps, antdRef) {
        const {
            mask,
            label,
            maskOptions: _maskOptions,
            value: _value,
            defaultValue,
            definitions,
            help,
            validateStatus,
            className,
            style,
            labelCol,
            wrapperCol,
            ...antdProps
        } = props;

        const innerRef = React.useRef<HTMLInputElement | null>(null);

        const maskOptions = useMemo(() => {
            return {
                mask,
                definitions: {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    ..._maskOptions?.definitions,
                    ...definitions,
                },
                lazy: false, // make placeholder always visible
                ..._maskOptions,
            } as IMaskOptions;
        }, [mask]);

        const placeholder = useMemo(() => {
            return IMask.createPipe({ ...maskOptions, lazy: false } as any)('');
        }, [maskOptions]);

        const imask = React.useRef<IMask.InputMask<any> | null>(null);

        const propValue =
            (typeof _value === 'string'
                ? _value
                : typeof _value === 'number'
                    ? String(_value)
                    : typeof defaultValue === 'number' ? String(defaultValue) : defaultValue) || '';

        const lastValue = React.useRef(propValue);
        const [value, setValue] = React.useState(propValue);

        React.useEffect(() => {
            setValue(propValue)
        }, [propValue])

        const _onEvent = React.useCallback((ev: any, execOnChangeCallback = false) => {
            const masked = imask.current;
            if (!masked) return;

            if (ev.target) {
                if (ev.target.value !== masked.value) {
                    masked.value = ev.target.value;
                    ev.target.value = masked.value;
                    lastValue.current = masked.value;
                }
            }

            Object.assign(ev, {
                maskedValue: masked.value,
                unmaskedValue: masked.unmaskedValue,
            });

            masked.updateValue();
            setValue(lastValue.current);

            if (execOnChangeCallback) {
                props.onChange?.(ev);
            }
        }, []);

        const _onAccept = React.useCallback((ev: any) => {
            if (!ev?.target) return;

            const input = innerRef.current;
            const masked = imask.current;
            if (!input || !masked) return;

            ev.target.value = masked.value;
            input.value = masked.value;
            lastValue.current = masked.value;

            _onEvent(ev, true);
        }, []);

        function updateMaskRef() {
            const input = innerRef.current;

            if (imask.current) {
                imask.current.updateOptions(maskOptions as any);
            }

            if (!imask.current && input) {
                imask.current = IMask<any>(input, maskOptions);
                imask.current.on('accept', _onAccept);
            }

            if (imask.current && imask.current.value !== lastValue.current) {
                imask.current.value = lastValue.current;
                imask.current.alignCursor();
            }
        }

        function updateValue(value: string) {
            lastValue.current = value;
            const input = innerRef.current;
            const masked = imask.current;
            if (!(input && masked)) return;
            masked.value = value;
            // updating value with the masked
            //   version (imask.value has a setter that triggers masking)
            input.value = masked.value;
            lastValue.current = masked.value;
        }

        React.useEffect(() => {
            updateMaskRef();

            return () => {
                imask.current?.destroy();
                imask.current = null;
            };
        }, [mask]);

        React.useEffect(() => {
            updateValue(propValue);
        }, [propValue]);

        const eventHandlers = React.useMemo(() => {
            return {
                onBlur(ev: any) {
                    _onEvent(ev);
                    props.onBlur?.(ev);
                },

                onPaste(ev: React.ClipboardEvent<HTMLInputElement>) {
                    ev.preventDefault();
                    lastValue.current = ev.clipboardData?.getData('text');

                    if (ev.target) {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-expect-error
                        ev.target.value = lastValue.current;
                    }

                    _onEvent(ev, true);
                    props.onPaste?.(ev);
                },

                onFocus(ev: any) {
                    _onEvent(ev);
                    props.onFocus?.(ev);
                },

                [KEY_PRESS_EVENT]: (ev: any) => {
                    _onEvent(ev, true);
                    props[KEY_PRESS_EVENT]?.(ev);
                },
            };
        }, []);

        return (
            <Form.Item
                style={style ? style : { }}
                className={className ? className : styles.ibinput}
                {...(labelCol ? { labelCol }: {})}
                {...(wrapperCol ? { wrapperCol }: {})}
                {...(validateStatus ? { validateStatus }: {})}
                {...(help ? { help }: {})}
                label={label}
                >
                <Input
                    placeholder={placeholder}
                    {...antdProps}
                    {...eventHandlers}
                    onChange={(ev) => _onEvent(ev, true)}
                    value={value}
                    ref={function handleInputMask(ref) {
                        if (antdRef) {
                            if (typeof antdRef == 'function') {
                                antdRef(ref);
                            } else {
                                antdRef.current = ref;
                            }
                        }

                        if (ref?.input) {
                            innerRef.current = ref.input;
                            if (!imask.current) {
                                updateMaskRef();
                            }
                        }
                    }}
                />
            </Form.Item>
        );
    }
);

function keyPressPropName() {
    if (typeof navigator !== 'undefined') {
        return navigator.userAgent.match(/Android/i)
            ? 'onBeforeInput'
            : 'onKeyPress';
    }
    return 'onKeyPress';
}

const KEY_PRESS_EVENT = keyPressPropName();

export default MaskedInput;

